import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { translations } from "@/i18n/translations";
import { ASSETS } from "@/lib/assets";
import PaymentModal from "@/components/PaymentModal";
import { ShareBar } from "@/components/ShareBar";
import { ReadingAudio } from "@/components/ReadingAudio";
import { toast } from "sonner";
import { Activity, Heart, Brain, Compass, Mountain, Lock, Download, Sparkles, Star, Loader2, Briefcase, Coins, HeartHandshake, Leaf, Users, Home, Check, Mail, MessageCircle } from "lucide-react";

const AREA_META = {
  career: { icon: Briefcase, color: "#60A5FA" },
  finance: { icon: Coins, color: "#FBBF24" },
  love: { icon: HeartHandshake, color: "#F472B6" },
  health: { icon: Leaf, color: "#34D399" },
  family: { icon: Users, color: "#C084FC" },
  property: { icon: Home, color: "#FDBA74" },
};
const AREA_KEYS = ["career", "finance", "love", "health", "family", "property"];

function AreaCard({ areaKey, area, pick }) {
  const m = AREA_META[areaKey];
  const Icon = m.icon;
  return (
    <div className="glass rounded-2xl p-6 border hover:scale-[1.01] transition-transform" style={{ borderColor: m.color + "40", background: `linear-gradient(135deg, ${m.color}14, transparent)` }} data-testid={`area-card-${areaKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center border" style={{ borderColor: m.color + "55", background: m.color + "18" }}>
          <Icon size={20} style={{ color: m.color }} />
        </div>
        <h3 className="font-serif text-lg" style={{ color: m.color }}>{pick({ en: area.title_en, hi: area.title_hi })}</h3>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed">{pick(area)}</p>
    </div>
  );
}

const LINE_META = {
  life: { icon: Activity, color: "#34D399", grad: "from-emerald-500/20 to-emerald-800/10", border: "border-emerald-500/30", text: "text-emerald-300" },
  heart: { icon: Heart, color: "#F472B6", grad: "from-pink-500/20 to-pink-800/10", border: "border-pink-500/30", text: "text-pink-300" },
  head: { icon: Brain, color: "#60A5FA", grad: "from-sky-500/20 to-sky-800/10", border: "border-sky-500/30", text: "text-sky-300" },
  fate: { icon: Compass, color: "#FBBF24", grad: "from-amber-500/20 to-amber-800/10", border: "border-amber-500/30", text: "text-amber-300" },
};

const TL_META = {
  past: { color: "#C084FC", grad: "from-purple-500/20 to-purple-800/10", border: "border-purple-500/30", text: "text-purple-300" },
  present: { color: "#22D3EE", grad: "from-cyan-500/20 to-cyan-800/10", border: "border-cyan-500/30", text: "text-cyan-300" },
  future: { color: "#F59E0B", grad: "from-amber-500/20 to-amber-800/10", border: "border-amber-500/30", text: "text-amber-300" },
};

function LineCard({ lineKey, line, pick }) {
  const m = LINE_META[lineKey];
  const Icon = m.icon;
  return (
    <div className={`glass rounded-2xl p-6 bg-gradient-to-br ${m.grad} ${m.border} border hover:scale-[1.01] transition-transform`} data-testid={`line-card-${lineKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center border" style={{ borderColor: m.color + "55", background: m.color + "18" }}>
          <Icon size={20} style={{ color: m.color }} />
        </div>
        <h3 className={`font-serif text-lg ${m.text}`}>{pick({ en: line.title_en, hi: line.title_hi })}</h3>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed">{pick(line)}</p>
    </div>
  );
}

function ReadingProgress({ t }) {
  const steps = t.upload.steps;
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const STEP_SEC = 14;
  const current = Math.min(Math.floor(elapsed / STEP_SEC), steps.length - 1);
  const pct = Math.min(96, Math.round(((current + (elapsed % STEP_SEC) / STEP_SEC) / steps.length) * 100));
  return (
    <div className="mt-8 max-w-md mx-auto text-left" data-testid="reading-progress">
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-5">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-purple-400 transition-[width] duration-1000" style={{ width: `${pct}%` }} data-testid="reading-progress-bar" />
      </div>
      <ul className="space-y-2.5">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={i} data-testid={`reading-step-${i}`} className={`flex items-center gap-3 text-sm transition-opacity duration-500 ${done ? "text-emerald-300/80" : active ? "text-amber-100" : "text-slate-500/60"}`}>
              <span className={`h-5 w-5 shrink-0 rounded-full grid place-items-center border text-[10px] ${done ? "border-emerald-400/60 bg-emerald-500/20" : active ? "border-amber-400 bg-amber-400/20" : "border-white/10"}`}>
                {done ? <Check size={12} /> : active ? <Loader2 size={12} className="animate-spin" /> : null}
              </span>
              <span className={active ? "font-medium" : ""}>{s}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Result() {
  const { id } = useParams();
  const { t, lang, pick, user } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [reading, setReading] = useState(location.state?.reading?.data ? location.state.reading : null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState("past");
  const [showPay, setShowPay] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const reportRef = useRef();
  const emailTriggered = useRef(false);
  const pdfCache = useRef({});
  const emailFnRef = useRef(null);

  const prebuildRef = useRef(null);
  prebuildRef.current = () => buildPdf(true).catch(() => {});
  useEffect(() => {
    if (reading?.data && reading.unlocked) {
      const id = setTimeout(() => prebuildRef.current?.(), 800);
      return () => clearTimeout(id);
    }
  }, [reading?.data, reading?.unlocked]);

  useEffect(() => {
    if (reading?.data && reading.unlocked && reading.report_emailed === false) {
      const id = setTimeout(() => emailFnRef.current?.(), 1500);
      return () => clearTimeout(id);
    }
  }, [reading?.data, reading?.unlocked, reading?.report_emailed]);

  useEffect(() => {
    let timer;
    let cancelled = false;
    let attempts = 0;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/palm/readings/${id}`);
        if (cancelled) return;
        setReading(data);
        if (data.status === "failed") { setFailed(true); return; }
        if (!data.data) {
          attempts += 1;
          if (attempts > 100) { setFailed(true); return; }
          timer = setTimeout(fetchOnce, 3000);
        }
      } catch (e) {
        if (e?.response?.status === 401) { navigate("/login"); return; }
        if (e?.response?.status === 404) { toast.error("Reading not found"); return; }
        attempts += 1;
        if (attempts > 100) { setFailed(true); return; }
        timer = setTimeout(fetchOnce, 3000);
      }
    };
    if (reading?.data) return;
    fetchOnce();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id]); // eslint-disable-line

  if (failed) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 text-center">
        <div>
          <p className="font-serif text-2xl text-amber-100 mb-4">🔮</p>
          <p className="text-slate-300 mb-6">{lang === "hi" ? "क्षमा करें, रीडिंग तैयार नहीं हो सकी। कृपया दोबारा प्रयास करें।" : "Sorry, we couldn't complete the reading. Please try again."}</p>
          <button onClick={() => navigate("/upload")} className="btn-gold rounded-full px-8 py-3">{t.common.tryAgain}</button>
        </div>
      </div>
    );
  }

  if (!reading?.data) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <div className="relative mx-auto h-28 w-28 mb-8">
            <div className="absolute inset-0 rounded-full border-2 border-amber-400/30 spin-slow" />
            <div className="absolute inset-3 rounded-full border border-purple-400/40 spin-slow" style={{ animationDirection: "reverse" }} />
            <Sparkles className="absolute inset-0 m-auto text-amber-300" size={36} />
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-amber-100">{t.upload.reading}</h2>
          <p className="mt-3 text-slate-400 max-w-md mx-auto">{t.upload.readingSub}</p>
          <ReadingProgress t={t} />
        </div>
      </div>
    );
  }

  const d = reading.data;
  const unlocked = reading.unlocked;

  const buildPdf = async (forEmail = false) => {
    const key = forEmail ? "email" : "download";
    if (pdfCache.current[key]) return pdfCache.current[key];
    const p = buildPdfUncached(forEmail).catch((e) => { delete pdfCache.current[key]; throw e; });
    pdfCache.current[key] = p;
    return p;
  };

  const buildPdfUncached = async (forEmail = false) => {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
    if (document.fonts?.ready) await document.fonts.ready;
    const render = async (scale, quality) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableW = pw - margin * 2;
      const paintBg = () => { pdf.setFillColor(11, 8, 26); pdf.rect(0, 0, pw, ph, "F"); };
      paintBg();
      let y = margin;
      const root = reportRef.current;
      const padL = parseFloat(getComputedStyle(root).paddingLeft) || 0;
      const contentW = root.clientWidth - padL * 2;
      const mmPerPx = usableW / contentW;
      const blocks = Array.from(root.children);
      for (const el of blocks) {
        const canvas = await html2canvas(el, { backgroundColor: "#0B081A", scale, useCORS: true });
        const img = canvas.toDataURL("image/jpeg", quality);
        const w = el.offsetWidth * mmPerPx;
        const x = margin + (el.offsetLeft - padL) * mmPerPx;
        const h = (canvas.height * w) / canvas.width;
        if (y + h > ph - margin && y > margin) {
          pdf.addPage();
          paintBg();
          y = margin;
        }
        if (h > ph - margin * 2) {
          // very tall block: slice across pages, masking the margins
          let drawn = 0;
          while (drawn < h) {
            pdf.addImage(img, "JPEG", x, y - drawn, w, h);
            pdf.setFillColor(11, 8, 26);
            pdf.rect(0, 0, pw, margin, "F");
            pdf.rect(0, ph - margin, pw, margin, "F");
            const space = ph - margin - y;
            if (drawn + space < h) {
              drawn += space;
              pdf.addPage(); paintBg(); y = margin;
            } else {
              y += (h - drawn) + 3;
              drawn = h;
            }
          }
        } else {
          pdf.addImage(img, "JPEG", x, y, w, h);
          y += h + 3;
        }
      }
      return pdf;
    };
    if (!forEmail) return render(1.6, 0.88);
    // Email proxy rejects attachments > ~2.5MB base64 — step down quality until it fits
    for (const [scale, q] of [[1.3, 0.7], [1.0, 0.6], [0.85, 0.5]]) {
      const pdf = await render(scale, q);
      if (pdf.output("datauristring").length <= 2_300_000) return pdf;
    }
    return render(0.8, 0.45);
  };

  const generatePdf = async () => {
    setPdfBusy(true);
    try {
      const pdf = await buildPdf();
      pdf.save(`AstroFriend-Palm-Reading-${reading.seeker_name || "Report"}.pdf`);
      toast.success(t.result.pdfReady);
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const emailReport = async (resend = false) => {
    if (emailTriggered.current) return;
    emailTriggered.current = true;
    try {
      toast.loading(t.result.emailing, { id: "email-report" });
      const pdf = await buildPdf(true);
      const b64 = pdf.output("datauristring");
      const { data } = await api.post("/report/email", { reading_id: reading.reading_id, pdf_base64: b64, base_url: window.location.origin, resend });
      setReading((r) => ({ ...r, report_emailed: true }));
      if (data.download_url) setDownloadUrl(data.download_url);
      if (data.already_sent) toast.dismiss("email-report");
      else toast.success(t.result.emailSent, { id: "email-report" });
    } catch (e) {
      emailTriggered.current = false;
      toast.error(e?.response?.data?.detail || t.result.emailFailed, { id: "email-report" });
    }
  };
  emailFnRef.current = emailReport;

  const shareWhatsApp = async () => {
    setWaBusy(true);
    toast.loading(t.result.preparingShare, { id: "wa-prep" });
    try {
      let url = downloadUrl;
      if (!url) {
        const { data } = await api.get(`/report/link/${reading.reading_id}`);
        url = data.download_url;
      }
      if (!url) {
        const pdf = await buildPdf(true);
        const { data } = await api.post("/report/store", { reading_id: reading.reading_id, pdf_base64: pdf.output("datauristring"), base_url: window.location.origin });
        url = data.download_url;
      }
      setDownloadUrl(url);
      const viewUrl = `${window.location.origin}/result/${reading.reading_id}`;
      const name = reading.seeker_name || user?.display_name || "";
      const msg = lang === "hi"
        ? `नमस्ते ${name} जी 🙏
आपकी AstroFriend हस्तरेखा रिपोर्ट तैयार है ✦

📄 PDF डाउनलोड: ${url}
🔮 रीडिंग देखें: ${viewUrl}

— AstroFriend, आपका ब्रह्मांडीय हस्तरेखा साथी`
        : `Namaste ${name} 🙏
Your AstroFriend palm reading report is ready ✦

📄 Download PDF: ${url}
🔮 View reading: ${viewUrl}

— AstroFriend, your cosmic palm companion`;
      const phone = user?.mobile ? `91${user.mobile}` : "";
      toast.dismiss("wa-prep");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.result.waFailed, { id: "wa-prep" });
    } finally {
      setWaBusy(false);
    }
  };

  const onUnlocked = () => {
    setReading((r) => ({ ...r, unlocked: true, report_emailed: false }));
    setShowPay(false);
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-14">
      <div className="text-center mb-10 rise-in">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300 mb-3">
          <Star size={12} className="fill-amber-300" /> AstroFriend
        </span>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-amber-50">{t.result.title}</h1>
        {reading.seeker_name && (
          <p className="mt-3 font-serif text-lg sm:text-xl text-amber-200" data-testid="seeker-greeting">{t.result.greeting.replace("{name}", reading.seeker_name)}</p>
        )}
      </div>

      {/* Overview (free) */}
      <div className="glass-strong rounded-3xl p-7 sm:p-9 mb-8">
        <h2 className="font-serif text-xl text-amber-200 mb-3">{t.result.overview}</h2>
        <p className="text-slate-200 leading-relaxed">{pick(d.overview)}</p>
      </div>

      {/* Lucky charms (free) */}
      {d.lucky && (
        <div className="grid grid-cols-3 gap-3 mb-10" data-testid="lucky-charms">
          {[
            { label: t.result.luckyNumber, value: d.lucky.number },
            { label: t.result.luckyColor, value: pick({ en: d.lucky.color_en, hi: d.lucky.color_hi }) },
            { label: t.result.luckyDay, value: pick({ en: d.lucky.day_en, hi: d.lucky.day_hi }) },
          ].map((c, i) => (
            <div key={i} className="glass rounded-2xl p-4 text-center">
              <div className="text-xs uppercase tracking-widest text-amber-300/70">{c.label}</div>
              <div className="font-serif text-xl text-amber-100 mt-1">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-serif text-2xl text-amber-100 mb-5">{t.result.lines}</h2>
      <div className="grid gap-5 sm:grid-cols-2 mb-4">
        {/* Life line free */}
        <LineCard lineKey="life" line={d.lines.life} pick={pick} />

        {unlocked ? (
          <>
            <LineCard lineKey="heart" line={d.lines.heart} pick={pick} />
            <LineCard lineKey="head" line={d.lines.head} pick={pick} />
            <LineCard lineKey="fate" line={d.lines.fate} pick={pick} />
          </>
        ) : (
          <div className="relative sm:col-span-1">
            <div className="blur-lock grid gap-5">
              <LineCard lineKey="heart" line={d.lines.heart} pick={pick} />
            </div>
          </div>
        )}
      </div>

      {/* Locked block */}
      {!unlocked && (
        <div className="relative rounded-3xl overflow-hidden mb-10" data-testid="locked-section">
          <div className="blur-lock p-7 space-y-4 glass rounded-3xl">
            <LineCard lineKey="head" line={d.lines.head} pick={pick} />
            <LineCard lineKey="fate" line={d.lines.fate} pick={pick} />
          </div>
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-t from-[#0B081A] via-[#0B081A]/80 to-transparent">
            <div className="text-center px-6">
              <div className="mx-auto mb-4 h-14 w-14 rounded-full grid place-items-center border border-amber-500/40 bg-purple-900/50">
                <Lock className="text-amber-300" size={24} />
              </div>
              <h3 className="font-serif text-2xl text-amber-100">{t.result.locked}</h3>
              <p className="mt-2 text-sm text-slate-300 max-w-md mx-auto">{t.result.lockedSub}</p>
              <button onClick={() => setShowPay(true)} data-testid="unlock-btn" className="btn-gold rounded-full px-8 py-3 mt-6 inline-flex items-center gap-2 gold-glow">
                <Sparkles size={18} /> {t.result.unlock}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mounts + timeline only when unlocked */}
      {unlocked && (
        <>
          <div className={`glass rounded-2xl p-6 border border-orange-500/25 bg-gradient-to-br from-orange-500/10 to-transparent mb-10`} data-testid="mounts-section">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center border border-orange-500/40 bg-orange-500/15">
                <Mountain className="text-orange-300" size={20} />
              </div>
              <h3 className="font-serif text-lg text-orange-200">{t.result.mounts}</h3>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{pick(d.mounts)}</p>
          </div>

          <h2 className="font-serif text-2xl text-amber-100 mb-5">{t.result.timeline}</h2>
          <div className="flex gap-2 mb-5" data-testid="timeline-tabs">
            {["past", "present", "future"].map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`tab-${k}`}
                className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all ${tab === k ? "btn-gold" : "glass text-slate-300"}`}
              >
                {t.result[k]}
              </button>
            ))}
          </div>
          <div className={`glass rounded-2xl p-7 bg-gradient-to-br ${TL_META[tab].grad} ${TL_META[tab].border} border`} data-testid="timeline-content">
            <p className="text-slate-200 leading-relaxed">{pick(d.timeline[tab])}</p>
          </div>

          {d.life_areas && (
            <div className="mt-12" data-testid="life-areas-section">
              <h2 className="font-serif text-2xl text-amber-100 mb-1">{t.result.lifeAreas}</h2>
              <p className="text-sm text-slate-400 mb-5">{t.result.lifeAreasSub}</p>
              <div className="grid gap-5 sm:grid-cols-2">
                {AREA_KEYS.filter((k) => d.life_areas[k]).map((k) => (
                  <AreaCard key={k} areaKey={k} area={d.life_areas[k]} pick={pick} />
                ))}
              </div>
            </div>
          )}

          {(d.lucky?.gem_en || d.lucky?.mantra_hi) && (
            <div className="grid grid-cols-2 gap-3 mt-8" data-testid="lucky-extra">
              {d.lucky.gem_en && (
                <div className="glass rounded-2xl p-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-amber-300/70">{t.result.luckyGem}</div>
                  <div className="font-serif text-xl text-amber-100 mt-1">{pick({ en: d.lucky.gem_en, hi: d.lucky.gem_hi })}</div>
                </div>
              )}
              {d.lucky.mantra_hi && (
                <div className="glass rounded-2xl p-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-amber-300/70">{t.result.luckyMantra}</div>
                  <div className="font-serif text-xl text-amber-100 mt-1">{d.lucky.mantra_hi}</div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="max-w-2xl mx-auto">
        <ReadingAudio readingId={reading.reading_id} unlocked={unlocked} />
      </div>

      <div className="mt-10 max-w-2xl mx-auto">
        <ShareBar />
      </div>

      {/* Actions */}
      {unlocked && user?.mobile && (
        <p className="text-center text-xs text-slate-400 mt-8 -mb-4 px-4" data-testid="whatsapp-hint">{t.result.whatsappHint.replace("{mobile}", `+91 ${user.mobile}`)}</p>
      )}
      <div className="action-row mt-8">
        <button onClick={() => navigate("/upload")} data-testid="new-reading-btn" className="btn-ghost-gold rounded-full px-6 py-3">
          {t.result.newReading}
        </button>
        {unlocked && (
          <button onClick={generatePdf} disabled={pdfBusy} data-testid="download-pdf-btn" className="btn-gold rounded-full px-8 py-3 flex items-center gap-2 gold-glow disabled:opacity-70">
            {pdfBusy ? <><Loader2 className="animate-spin" size={18} /> {t.result.preparing}</> : <><Download size={18} /> {t.result.downloadPdf}</>}
          </button>
        )}
        {unlocked && (
          <button onClick={() => { emailTriggered.current = false; emailReport(true); }} data-testid="email-pdf-btn" className="btn-ghost-gold rounded-full px-6 py-3 flex items-center gap-2">
            <Mail size={18} /> {t.result.emailPdf}
          </button>
        )}
        {unlocked && (
          <button onClick={shareWhatsApp} disabled={waBusy} data-testid="whatsapp-report-btn" className="rounded-full px-6 py-3 flex items-center gap-2 text-white font-medium disabled:opacity-70 transition-transform hover:scale-[1.02]" style={{ background: "#25D366" }}>
            {waBusy ? <Loader2 className="animate-spin" size={18} /> : <MessageCircle size={18} />} {t.result.whatsappReport}
          </button>
        )}
      </div>

      {showPay && <PaymentModal readingId={reading.reading_id} onSuccess={onUnlocked} onClose={() => setShowPay(false)} />}

      {/* Hidden PDF report — bilingual (Hindi first, English below) + spiritual */}
      <div style={{ position: "fixed", left: "-10000px", top: 0 }} aria-hidden="true">
        <div ref={reportRef} style={{ width: "800px", background: "#0B081A", color: "#F8FAFC", padding: "48px", fontFamily: "'Tiro Devanagari Hindi', 'Outfit', sans-serif" }}>
          <div style={{ textAlign: "center", borderBottom: "1px solid rgba(245,158,11,0.3)", paddingBottom: "20px", marginBottom: "10px" }}>
            <img src={ASSETS.logo} crossOrigin="anonymous" alt="logo" style={{ width: 74, height: 74, borderRadius: "50%", margin: "0 auto 10px" }} />
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: 32, color: "#FBBF24", fontWeight: 700 }}>AstroFriend</div>
            <div style={{ color: "#C4B5FD", fontSize: 13, marginTop: 4 }}>✦ ☾ आपकी हस्तरेखा रिपोर्ट · Your Palm Reading Report ☽ ✦</div>
          </div>
          <div style={{ textAlign: "center", color: "#C084FC", fontSize: 20, letterSpacing: 8, margin: "14px 0 18px" }}>✦ ☾ ॐ ☽ ✦</div>
          {reading.seeker_name && (
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: 24, color: "#FDE68A" }}>नमस्ते {reading.seeker_name} जी 🙏</div>
              <div style={{ color: "#C4B5FD", fontSize: 13, marginTop: 6 }}>{reading.seeker_name}, यह रिपोर्ट विशेष रूप से आपकी हथेली के आधार पर तैयार की गई है · This report is prepared specially from your palm</div>
            </div>
          )}
          <img src={ASSETS.palmDiagram} crossOrigin="anonymous" alt="palm" style={{ width: "250px", display: "block", margin: "0 auto 26px", borderRadius: 14, border: "1px solid rgba(245,158,11,0.3)" }} />

          <PdfSection titleHi="सार" titleEn="Overview" hi={d.overview.hi} en={d.overview.en} color="#FBBF24" />
          {["life", "heart", "head", "fate"].map((k) => (
            <PdfSection key={k} titleHi={d.lines[k].title_hi} titleEn={d.lines[k].title_en} hi={d.lines[k].hi} en={d.lines[k].en} color={LINE_META[k].color} />
          ))}
          <PdfSection titleHi="पर्वत (माउंट्स)" titleEn="Mounts" hi={d.mounts.hi} en={d.mounts.en} color="#FDBA74" />

          <div style={{ textAlign: "center", color: "#F59E0B", fontFamily: "'Cinzel', serif", fontSize: 20, margin: "26px 0 14px" }}>☾ भूत · वर्तमान · भविष्य / Past · Present · Future ☽</div>
          <PdfSection titleHi="भूतकाल" titleEn="Past" hi={d.timeline.past.hi} en={d.timeline.past.en} color="#C084FC" />
          <PdfSection titleHi="वर्तमान" titleEn="Present" hi={d.timeline.present.hi} en={d.timeline.present.en} color="#22D3EE" />
          <PdfSection titleHi="भविष्य" titleEn="Future" hi={d.timeline.future.hi} en={d.timeline.future.en} color="#F59E0B" />

          {d.life_areas && (
            <>
              <div style={{ textAlign: "center", color: "#F59E0B", fontFamily: "'Cinzel', serif", fontSize: 20, margin: "26px 0 14px" }}>☾ जीवन के क्षेत्र / Life Areas ☽</div>
              {AREA_KEYS.filter((k) => d.life_areas[k]).map((k) => (
                <PdfSection key={k} titleHi={d.life_areas[k].title_hi} titleEn={d.life_areas[k].title_en} hi={d.life_areas[k].hi} en={d.life_areas[k].en} color={AREA_META[k].color} />
              ))}
            </>
          )}

          {d.lucky && (
            <div style={{ display: "flex", gap: 10, margin: "8px 0 20px", flexWrap: "wrap" }}>
              {[
                { l: "शुभ अंक / Number", v: d.lucky.number },
                { l: "शुभ रंग / Color", v: `${d.lucky.color_hi} / ${d.lucky.color_en}` },
                { l: "शुभ दिन / Day", v: `${d.lucky.day_hi} / ${d.lucky.day_en}` },
                ...(d.lucky.gem_en ? [{ l: "शुभ रत्न / Gemstone", v: `${d.lucky.gem_hi} / ${d.lucky.gem_en}` }] : []),
                ...(d.lucky.mantra_hi ? [{ l: "मंत्र / Mantra", v: d.lucky.mantra_hi }] : []),
              ].map((c, i) => (
                <div key={i} style={{ flex: "1 1 30%", textAlign: "center", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 12, padding: "12px 8px" }}>
                  <div style={{ fontSize: 10, color: "#FCD34D", letterSpacing: 1, textTransform: "uppercase" }}>{c.l}</div>
                  <div style={{ fontFamily: "'Cinzel', serif", fontSize: 16, color: "#FDE68A", marginTop: 4 }}>{c.v}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ textAlign: "center", color: "#C084FC", fontSize: 18, letterSpacing: 6, margin: "10px 0" }}>✦ ☾ ॐ ☽ ✦</div>
          <div style={{ marginTop: 14, paddingTop: 16, borderTop: "1px solid rgba(245,158,11,0.2)", fontSize: 10.5, color: "#64748B", textAlign: "center", lineHeight: 1.7 }}>
            <div style={{ marginBottom: 4 }}>{translations.hi.footer.disclaimer}</div>
            <div>{translations.en.footer.disclaimer}</div>
            <div style={{ marginTop: 8, color: "#FBBF24" }}>✦ AstroFriend · आपका ब्रह्मांडीय हस्तरेखा साथी ✦</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PdfSection({ titleHi, titleEn, hi, en, color }) {
  return (
    <div style={{ marginBottom: 20, background: "rgba(23,15,52,0.6)", border: `1px solid ${color}44`, borderRadius: 14, padding: "18px 20px" }}>
      <div style={{ fontFamily: "'Cinzel', serif", color, fontSize: 17, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>✦</span>
        <span>{titleHi}</span>
        <span style={{ color: "#94A3B8", fontSize: 13 }}>/ {titleEn}</span>
      </div>
      <div style={{ color: "#EDE9FE", fontSize: 14.5, lineHeight: 1.75, marginBottom: 10 }}>{hi}</div>
      <div style={{ color: "#CBD5E1", fontSize: 13.5, lineHeight: 1.7, borderTop: "1px dashed rgba(148,163,184,0.22)", paddingTop: 10 }}>{en}</div>
    </div>
  );
}
